import { FC, forwardRef, RefObject, useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Button, Popover, PopoverAnchor, PopoverContent, rem, Stack, Text, useMediaQuery } from '@bonusly/design-system';

import { BizyHintBubble, promptGradientBorderSx } from '../../../../bizy';

export type CommentAssistCoachingPhase = 'none' | 'short' | 'ready';

/**
 * Where the parked bee perches on the comment card.
 *  - `top-right`: desktop — perched on the card's top-right edge, bubble to the side.
 *  - `bottom-right`: mobile — above the Add comment button, right of the `+` button,
 *    bubble stacked on top of the bee.
 */
export type CommentAssistBeePlacement = 'top-right' | 'bottom-right';

export interface BizyCommentAssistBeeHandle {
	/** Opens the bee's tap panel programmatically (e.g. via the `/` shortcut). */
	open: () => void;
}

interface BizyCommentAssistBeeProps {
	bonusId: string;
	/** Invisible flight-marker / tap target the bubble is anchored to. */
	anchorRef: RefObject<HTMLButtonElement>;
	/** True once the bee has finished flying and parked on this box. */
	hasLanded: boolean;
	/** Opens the in-box cold suggestion picker. */
	onGiveMeIdeas?: () => void;
	/** Opens draft-aware refine options. */
	onMakeItShine?: () => void;
	/** Debounced draft-length coaching phase for the resting hint. */
	coachingPhase?: CommentAssistCoachingPhase;
	/** Hide the hint/panel while the in-box suggestion picker is open. */
	suppressBubble?: boolean;
	/** After a suggestion pick, keep the resting/coaching bubble off until the bee is tapped. */
	hideRestingHint?: boolean;
	/** After a Make it shine pick, hide refine until the draft is cleared (start over). */
	suppressMakeItShine?: boolean;
	/** Where the bee perches on the comment card. Defaults to the desktop top-right perch. */
	placement?: CommentAssistBeePlacement;
	/**
	 * True when the composer is rendered inside a modal. The default right-gutter
	 * bubble overflows the narrow modal and adds a horizontal scrollbar, so we
	 * flip it to the left (over the composer) — same as the narrow-viewport flip.
	 */
	inModal?: boolean;
}

const BUBBLE_OFFSET: [number, number] = [-40, -10];
// Mobile bubble sits above the bee; distance clears the 80px overlay bee (centered
// on the 40px anchor, it extends ~20px above the anchor's top) so the tail touches
// the bee instead of overlapping it.
const BUBBLE_OFFSET_MOBILE: [number, number] = [0, 24];
const BEE_ANCHOR_RIGHT = rem(-24);
// Mobile perch: above the Add comment button (submit row + card padding ≈ 64px).
// Horizontally the bee sits on the right side of the full-width `+` button — its
// right edge aligns with the `+` button's right edge (card padding, 12px in) rather
// than overhanging the card's far-right edge, so `right` is the anchor offset that
// lands the 80px overlay bee's right edge at ~12px from the card edge.
// Mobile perch: above the Add comment button (submit row + card padding ≈ 64px)
// and perched on the card's right edge so the bee sits on the right side of the
// full-width `+` button.
const BEE_ANCHOR_MOBILE_BOTTOM = rem(64);
const BEE_ANCHOR_MOBILE_RIGHT = rem(8);
const NARROW_VIEWPORT_QUERY = '(max-width: 90rem)';

const COMMENT_BUBBLE_BORDER = 'rgba(107, 116, 133, 0.08)';
const COMMENT_BUBBLE_SHADOW = '0px 3px 1.5px 0px rgba(0, 0, 0, 0.25)';

const HINT_TEXT_SX = {
	color: 'text.default',
	fontSize: '14px',
	fontWeight: 500,
	lineHeight: '18px',
	letterSpacing: '-0.14px',
} as const;

const ShineButton: FC<{ label: string; onClick: () => void; 'data-testid'?: string }> = ({ label, onClick, 'data-testid': testId }) => (
	<Button
		onClick={onClick}
		w="full"
		variant="outline"
		size="sm"
		borderRadius="12px"
		color="text.softest"
		fontWeight="normal"
		sx={promptGradientBorderSx}
		_hover={{ color: 'text.default' }}
		data-testid={testId}
	>
		{label}
	</Button>
);

/**
 * Parked-bee affordance for inline comment assist.
 *
 * On desktop (`placement="top-right"`) Bizy perches on the top-right of the comment
 * box; the hint/panel bubble sits in the right gutter when there is room, and on
 * narrow viewports flips to Bizy's left (over the composer) with a bottom-right tail.
 *
 * On mobile (`placement="bottom-right"`) Bizy perches above the Add comment button,
 * on the right side of the full-width `+` button, and the bubble stacks on top of
 * her (top-end placement, bottom-right tail).
 */
export const BizyCommentAssistBee = forwardRef<BizyCommentAssistBeeHandle, BizyCommentAssistBeeProps>(function BizyCommentAssistBee(
	{
		bonusId,
		anchorRef,
		hasLanded,
		onGiveMeIdeas,
		onMakeItShine,
		coachingPhase = 'none',
		suppressBubble = false,
		hideRestingHint = false,
		suppressMakeItShine = false,
		placement = 'top-right',
		inModal = false,
	},
	ref
) {
	const { t } = useTranslation('recognition', { keyPrefix: 'comment.bizy_assist' });
	const { track } = useAnalytics();
	const [engaged, setEngaged] = useState(false);
	const panelId = useId();
	const [isNarrowViewport] = useMediaQuery(NARROW_VIEWPORT_QUERY);
	const isMobilePlacement = placement === 'bottom-right';
	// Put the bubble on the left (over the composer) on narrow viewports and
	// inside modals; the right-gutter placement overflows there and scrolls.
	const preferLeftBubble = isNarrowViewport || inModal;
	const showMakeItShine = coachingPhase === 'ready' && !suppressMakeItShine;

	const hasTrackedShown = useRef(false);
	useEffect(() => {
		if (!hasLanded || hasTrackedShown.current) return;
		hasTrackedShown.current = true;
		track(AnalyticsEvent.CommentAssist.BeeShown, { bonusId });
	}, [hasLanded, track, bonusId]);

	const trackedCoachingPhase = useRef<CommentAssistCoachingPhase | null>(null);
	useEffect(() => {
		if (coachingPhase === 'none') {
			trackedCoachingPhase.current = null;
			return;
		}
		// Don't count coaching as "shown" while the bubble is suppressed after a pick.
		if (suppressBubble || hideRestingHint || !hasLanded) return;
		if (trackedCoachingPhase.current === coachingPhase) return;
		trackedCoachingPhase.current = coachingPhase;
		track(AnalyticsEvent.CommentAssist.CoachingHintShown, {
			bonusId,
			threshold: coachingPhase,
		});
	}, [suppressBubble, hideRestingHint, hasLanded, coachingPhase, track, bonusId]);

	useEffect(() => {
		if (suppressBubble) setEngaged(false);
	}, [suppressBubble]);

	const handleBeeTap = useCallback(() => {
		if (suppressBubble) return;
		setEngaged((wasEngaged) => {
			if (!wasEngaged) track(AnalyticsEvent.CommentAssist.BeeTapped, { bonusId });
			return !wasEngaged;
		});
	}, [track, bonusId, suppressBubble]);

	// Programmatic open (used by the `/` shortcut in the comment composer so
	// typing `/` engages the bee's panel as if it were tapped). Respects
	// `suppressBubble` so it can't yank the panel open over the suggestion picker.
	const open = useCallback(() => {
		if (suppressBubble) return;
		setEngaged((wasEngaged) => {
			if (!wasEngaged) track(AnalyticsEvent.CommentAssist.BeeTapped, { bonusId });
			return true;
		});
	}, [track, bonusId, suppressBubble]);

	useImperativeHandle(ref, () => ({ open }), [open]);

	const handleGiveIdeas = useCallback(() => {
		track(AnalyticsEvent.CommentAssist.GiveIdeasClicked, { bonusId });
		onGiveMeIdeas?.();
		setEngaged(false);
	}, [track, bonusId, onGiveMeIdeas]);

	const handleMakeItShine = useCallback(() => {
		track(AnalyticsEvent.CommentAssist.MakeItShineClicked, { bonusId });
		onMakeItShine?.();
		setEngaged(false);
	}, [track, bonusId, onMakeItShine]);

	// After a pick, hideRestingHint keeps the bubble closed until the bee is
	// tapped (engaged). Coaching must not reopen it with a new Make it shine.
	const bubbleOpen = !suppressBubble && (engaged || (hasLanded && !hideRestingHint));
	const interactiveBubble = engaged || (showMakeItShine && !hideRestingHint);

	const restingCopy = coachingPhase === 'short' ? t('coaching_short') : coachingPhase === 'ready' ? t('coaching_ready') : t('resting_hint');

	const popoverPlacement = isMobilePlacement ? 'top-end' : preferLeftBubble ? 'left-end' : 'right-end';
	const bubbleTail = isMobilePlacement ? 'bottom-right' : preferLeftBubble ? 'bottom-right' : 'bottom-left';
	const bubbleOffset = isMobilePlacement ? BUBBLE_OFFSET_MOBILE : BUBBLE_OFFSET;
	const bubbleKey = `${engaged ? 'panel' : coachingPhase}-${isMobilePlacement ? 'top' : preferLeftBubble ? 'left' : 'right'}`;

	return (
		<Popover
			isOpen={bubbleOpen}
			onClose={() => setEngaged(false)}
			placement={popoverPlacement}
			offset={bubbleOffset}
			flip={false}
			preventOverflow={false}
			autoFocus={false}
			returnFocusOnClose={false}
			isLazy
			closeOnBlur={false}
		>
			<PopoverAnchor>
				<Box
					as="button"
					type="button"
					ref={anchorRef}
					onClick={handleBeeTap}
					aria-label={t('aria_label')}
					aria-haspopup="dialog"
					aria-expanded={engaged}
					aria-controls={engaged ? panelId : undefined}
					position="absolute"
					{...(isMobilePlacement ? { bottom: BEE_ANCHOR_MOBILE_BOTTOM, right: BEE_ANCHOR_MOBILE_RIGHT } : { top: 2, right: inModal ? 0 : BEE_ANCHOR_RIGHT })}
					width={rem(40)}
					height={rem(40)}
					bg="transparent"
					zIndex={2}
				/>
			</PopoverAnchor>
			<BizyHintBubble
				key={bubbleKey}
				as={PopoverContent}
				id={panelId}
				role={engaged || (showMakeItShine && !hideRestingHint) ? 'dialog' : 'status'}
				tail={bubbleTail}
				width="auto"
				maxW={rem(210)}
				borderColor={COMMENT_BUBBLE_BORDER}
				boxShadow={COMMENT_BUBBLE_SHADOW}
				pointerEvents={interactiveBubble ? 'auto' : 'none'}
				_focus={{ boxShadow: COMMENT_BUBBLE_SHADOW }}
			>
				{engaged ? (
					<Stack gap={2} align="stretch">
						<Text sx={HINT_TEXT_SX} w="full">
							{t('panel_prompt')}
						</Text>
						<ShineButton label={t('give_ideas')} onClick={handleGiveIdeas} data-testid="bizy-comment-assist-give-ideas" />
						{showMakeItShine && <ShineButton label={t('make_it_shine')} onClick={handleMakeItShine} data-testid="bizy-comment-assist-make-it-shine" />}
					</Stack>
				) : showMakeItShine ? (
					<Stack gap={2} align="stretch">
						<Text sx={HINT_TEXT_SX} w="full">
							{restingCopy}
						</Text>
						<ShineButton label={t('make_it_shine')} onClick={handleMakeItShine} data-testid="bizy-comment-assist-make-it-shine" />
					</Stack>
				) : (
					<Text sx={HINT_TEXT_SX}>{restingCopy}</Text>
				)}
			</BizyHintBubble>
		</Popover>
	);
});
